import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import ReusableTable from "components/Tables/ReusableTable";
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import { Flex, Box, Text, Badge } from "@chakra-ui/react";
import { EllipsisVertical, Landmark, DoorOpen } from "lucide-react";
import { useHistory } from "react-router-dom";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import ViewTransaction from "./components/ViewTransaction";
import ReusableDrawer from "components/Drawers/ReusableDrawer";
import { formatBWP } from "./utils/formatBWP";
import MonitorPaymentGateway from "./components/MonitorPaymentGateway";
import ExportButton from "components/Reports/ExportCSV";
import { fetchTransactionsAction } from "state/actions/transactionActions";
import { formatDate } from "utils/dateFormatter";

const TransactionManagement = () => {


  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isViewTransactionDrawerOpen, setViewTransactionDrawerOpen] = useState(false);
  const [isMonitorPaymentGatewayDrawerOpen, setMonitorPaymentGatewayDrawerOpen] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const { transactions, loading } = useSelector((state) => state.transactions);

  useEffect(() => {
    dispatch(fetchTransactionsAction());
  }, [dispatch]);

  const refetchTransactions = () => {
  dispatch(fetchTransactionsAction());
};


  const handleOpenViewTransactionDrawer = (transaction) => {
    setSelectedTransaction(transaction);
    setViewTransactionDrawerOpen(true);
  };

  const handleCloseViewTransactionDrawer = () => {
    setSelectedTransaction(null);
    setViewTransactionDrawerOpen(false);
  };

  const handleOpenMonitorGatewayDrawer = () => {
    setMonitorPaymentGatewayDrawerOpen(true);
  };

  const handleCloseMonitorGatewayDrawer = () => {
    setMonitorPaymentGatewayDrawerOpen(false);
  };

  const tableData = useMemo(() => {
    if (!Array.isArray(transactions)) return [];

    return transactions.map((txn) => {
      const sender = txn.sender || {};
      const packageData = txn.package || {};
      const driver = txn.driver_user || {};

      const senderName = sender.first_name && sender.last_name
        ? `${sender.first_name} ${sender.last_name}`
        : sender.email || 'Unknown Sender';

      const driverName = driver.first_name && driver.last_name
        ? `${driver.first_name} ${driver.last_name}`
        : driver.email || 'Unknown Driver';

      const senderContact = sender.phone_number || sender.email || 'No contact info';
      const driverContact = driver.phone_number || driver.email || 'No contact info';

      return {
        transaction_id: txn.transaction_id,
        paygate_transaction_id: txn.paygate_transaction_id,
        user_id: txn.user_id,
        sender_name: senderName,
        sender_contact: senderContact,
        driver_name: driverName,
        driver_contact: driverContact,
        package_id: txn.package_id,
        package_name: packageData.package_description || 'N/A',
        amount: txn.amount,
        commission: txn.commission ?? 0,
        payment_method: txn.payment_method,
        transaction_status: txn.status,
        transaction_date: formatDate(txn.created_at),
        completion_date: formatDate(txn.updated_at),
        payment_gateway_reference: txn.transaction_token,
        original: txn,
      };
    });
  }, [transactions]);





  const generateMenuItems = (row) => [
    {
      icon: <Landmark size={16} />,
      text: "View Transaction",
      onClick: () => handleOpenViewTransactionDrawer(row.original),
    },
    {
      icon: <DoorOpen size={16} />,
      text: "Monitor Gateway",
      onClick: () => handleOpenMonitorGatewayDrawer(row.original),
    },
  ];

  const columnsData = [
    {
      Header: "Sender",
      accessor: "sender_name",
    },
    {
      Header: "Package",
      accessor: "package_name",
    },
    {
      Header: "Amount",
      accessor: "amount",
      Cell: ({ value }) => formatBWP(value),
    },
    {
      Header: "Status",
      accessor: "transaction_status",
      Cell: ({ value }) => {
        let colorScheme;

        if (value === "Completed") {
          colorScheme = "green";
        } else if (value === "Pending") {
          colorScheme = "orange";
        } else if (value === "Failed") {
          colorScheme = "red";
        } else if (value === "Refunded") {
          colorScheme = "blue";
        } else {
          colorScheme = "gray";
        }

        return (
          <Badge colorScheme={colorScheme} borderRadius="full" px={2}>
            {value}
          </Badge>
        );
      },
    },

      {
      Header: "Created",
      accessor: "transaction_date",
    },
    {
      Header: "Actions",
      accessor: "moreOptions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={generateMenuItems(row)}
        />
      ),
    },
  ];

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <Box p={6}>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          {loading ? (
            <Skeleton height="20px" width="150px" />
          ) : (
            <Text fontSize="md" fontWeight="bold">
              All Transactions: {transactions.length}
            </Text>
          )}

          {loading ? (
            <Skeleton height="30px" width="80px" />
          ) : (
            <ExportButton
              data={transactions}
              fileName="transactions"
              height={"30px"}
              width={"80px"}
            />
          )}
        </Flex>
      </Box>
      <ReusableTable columnsData={columnsData} tableData={tableData} isLoading={loading} />

      {/* View Transaction Drawer */}
      <ReusableDrawer
        isOpen={isViewTransactionDrawerOpen}
        onClose={handleCloseViewTransactionDrawer}
        size={"lg"}
        headerContent={<h2 style={{ fontSize: "md" }}>View Transaction</h2>}
        bodyContent={
          selectedTransaction && <ViewTransaction transaction={selectedTransaction}  onReload={refetchTransactions} />
        }
      />

      {/* Monitor Payment Gateway Drawer */}
      <ReusableDrawer
        isOpen={isMonitorPaymentGatewayDrawerOpen}
        onClose={handleCloseMonitorGatewayDrawer}
        size={"lg"}
        headerContent={<h2 style={{ fontSize: "md" }}>Monitor Payment Gateway</h2>}
        bodyContent={<MonitorPaymentGateway />}
      />
    </Flex>
  );
};

export default TransactionManagement;
